import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from as observableFrom, of as observableOf, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';

import { PolicyDocument } from '@amfam/policy/models/lib/models/policy-document';
import { PolicySummary } from '@amfam/policy/models/lib/models/policy-summary';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';

import { PolicyDocumentService } from '../../services/policy-documents.service';
import { PolicyService } from '../../services/policy.service';
import { SummariesAction, SummariesActionTypes } from '../summaries/summaries.actions';
import {
  LoadDocuments,
  LoadDocumentsComplete,
  LoadDocumentsError,
  LoadDocumentsSuccess
} from './documents.actions';

export const policySummariesLoaded = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(SummariesActionTypes.SummariesLoadSuccessType),
      map((action: SummariesAction) => action.payload),
      map(payload => LoadDocuments(payload))
    );
  },
  { functional: true }
);

export const loadPolicyDocuments = createEffect(
  (
    actions$ = inject(Actions),
    policyDocumentService = inject(PolicyDocumentService),
    policyService = inject(PolicyService),
    utilService = inject(UtilService)
  ) => {
    return actions$.pipe(
      ofType(LoadDocuments),
      map(action => action.payload),
      switchMap((payload: PolicySummary[]) =>
        observableFrom(
          payload.filter(policySummary =>
            policyService.isPolicyTypeSupported(policySummary.generalizedProductType)
          )
        ).pipe(
          mergeMap(policySummary => {
            return policyDocumentService
              .getDocumentList(
                policySummary.policyNumber,
                policySummary.sourceSystem,
                policySummary.generalizedProductType,
                policySummary.sourcePath,
                policySummary.policyPartnerId
              )
              .pipe(catchError(() => of([])));
          }),
          mergeMap(p => p),
          toArray(),
          map(policyDocuments =>
            (policyDocuments as PolicyDocument[]).map(doc => ({
              ...doc,
              policyNumber: utilService.onlyAlphanumeric(doc.policyNumber)
            }))
          ),
          catchError(() => of([]))
        )
      ),
      map(policyDocuments => LoadDocumentsSuccess(policyDocuments)),
      catchError(error => observableOf(LoadDocumentsError(error)))
    );
  },
  { functional: true }
);

export const policyDocumentsLoaded = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(LoadDocumentsSuccess, LoadDocumentsError),
      map(action => action.payload),
      map(() => LoadDocumentsComplete())
    );
  },
  { functional: true }
);
